import { Link } from "react-router-dom";

import type { OrderListItem } from "../../api/types";
import { formatDateTime, formatMoney } from "../../components/ui";
import { OrderStatusBadge } from "../OrderStatusBadge";
import { paymentMethodLabel } from "../orderStatus";

/**
 * One page of orders.
 *
 * Every column is a field the listing endpoint actually returns. Nothing is
 * derived that the backend did not send, and no total is recomputed here: the
 * amount shown is the one stored against the order.
 *
 * WHAT IS DELIBERATELY NOT A COLUMN
 * `payment_status`. The field exists on the detail contract and is always null
 * — cash on delivery records no payment attempt — so a column for it would be
 * an empty promise of payment tracking this shop does not have. The method IS
 * shown, because that one is real.
 */
export function OrdersTable({ items }: { items: OrderListItem[] }) {
  return (
    <div className="orders__scroll">
      <table className="table">
        <thead>
          <tr>
            <th scope="col">Order</th>
            {/* "Date placed", not "Placed": `placed` is also a lifecycle state,
                and this column sits two along from the one whose badge says
                exactly that. */}
            <th scope="col">Date placed</th>
            <th scope="col">Customer</th>
            <th scope="col">Items</th>
            <th scope="col">Status</th>
            <th scope="col" className="num">
              Total
            </th>
            <th scope="col">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((order) => (
            <OrderRow key={order.id} order={order} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OrderRow({ order }: { order: OrderListItem }) {
  return (
    <tr>
      <td>
        {/* The number is the way into the order. A real link rather than a
            click handler on the row, so Open can sit in the same row without
            the row swallowing the click. */}
        <Link className="orders__number" to={`/orders/${order.id}`}>
          {order.order_number}
        </Link>
        <span className="orders__method">
          {paymentMethodLabel(order.payment_method)}
        </span>
      </td>

      <td className="muted">{formatDateTime(order.created_at)}</td>

      <td>
        <span className="orders__customer">{order.customer_name}</span>
        {/* tel: so an operator can call the customer from the row they are
            already looking at, which is the next thing they do about half the
            time this screen is open. */}
        <a className="orders__phone" href={`tel:${order.customer_phone}`}>
          {order.customer_phone}
        </a>
      </td>

      <td className="orders__items">
        {order.total_quantity} item{order.total_quantity === 1 ? "" : "s"}
        {order.item_count !== order.total_quantity ? (
          <span className="orders__lines">
            {order.item_count} line{order.item_count === 1 ? "" : "s"}
          </span>
        ) : null}
      </td>

      <td>
        <OrderStatusBadge status={order.status} />
      </td>

      <td className="num">{formatMoney(order.total_minor, order.currency)}</td>

      <td>
        <Link
          className="btn btn--sm"
          to={`/orders/${order.id}`}
          // Named for a screen reader, which hears every row's link as "Open"
          // otherwise and cannot tell which order it would open.
          aria-label={`Open order ${order.order_number}`}
        >
          Open
        </Link>
      </td>
    </tr>
  );
}
